import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Printer, Award } from "lucide-react";

export default function Certificate() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    api.get("/certificate")
      .then(({ data }) => setCert(data))
      .catch((e) => {
        const detail = e?.response?.data?.detail || "Certificate not available";
        setError(detail);
      });
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen bg-[#F7F6F0] p-12 font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F6F0] flex items-center justify-center px-6" data-testid="certificate-error">
        <div className="card-flat p-12 max-w-lg w-full text-center">
          <Award size={48} className="text-[#C56B52] mx-auto mb-6" strokeWidth={1.5} />
          <div className="overline mb-3">Not ready yet</div>
          <h1 className="font-serif-display text-3xl text-[#121614] leading-tight mb-4">
            {error === "Course not yet complete" ? "Finish every lesson to unlock your certificate." : error}
          </h1>
          <p className="text-[#4B534E] mb-6">Complete all 26 lessons across the 12 modules and the certificate will be issued to your name.</p>
          <button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="cert-back-dashboard">
            <ArrowLeft size={14} /> Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!cert) return <div className="min-h-screen bg-[#F7F6F0] p-12 font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Loading…</div>;

  const issued = new Date(cert.issued_at);
  const dateStr = issued.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-[#F7F6F0]" data-testid="certificate-page">
      {/* Controls (hidden on print) */}
      <div className="print:hidden border-b border-[#E0DCD1] bg-[#F7F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-sm text-[#4B534E] hover:text-[#1A3626] inline-flex items-center gap-2" data-testid="cert-back-btn">
            <ArrowLeft size={14} /> Dashboard
          </button>
          <button onClick={() => window.print()} className="btn-primary text-sm py-2 px-4" data-testid="cert-print-btn">
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Certificate */}
      <div className="max-w-5xl mx-auto p-6 lg:p-16 print:p-0">
        <div className="bg-white border-[3px] border-[#1A3626] p-10 lg:p-16 relative overflow-hidden print:border-[3px]" data-testid="certificate-body">
          {/* Inner ornamental frame */}
          <div className="border border-[#C56B52] p-8 lg:p-14 text-center">
            <Award size={56} className="text-[#C56B52] mx-auto mb-8" strokeWidth={1.25} />

            <div className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[#4B534E] mb-6">
              Holloway Wealth Lab · Certificate of Completion
            </div>

            <div className="font-serif-display text-2xl lg:text-3xl text-[#4B534E] mb-4 italic">
              This is to certify that
            </div>

            <h1 className="font-serif-display text-5xl lg:text-7xl text-[#1A3626] leading-tight mb-8" data-testid="cert-name">
              {cert.name}
            </h1>

            <p className="font-serif-display text-xl lg:text-2xl text-[#121614] max-w-2xl mx-auto leading-snug mb-2 italic">
              has successfully completed the twelve-module masterclass
            </p>

            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#121614] mb-10" data-testid="cert-course">
              {cert.course_title}
            </h2>

            <div className="flex flex-wrap justify-center gap-12 lg:gap-20 mt-12 pt-10 border-t border-[#E0DCD1]">
              <div className="text-center">
                <div className="font-serif-display text-2xl text-[#1A3626] italic" data-testid="cert-instructor">{cert.instructor}</div>
                <div className="border-t border-[#121614] mt-2 pt-2 font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#4B534E]">Instructor</div>
              </div>
              <div className="text-center">
                <div className="font-serif-display text-2xl text-[#1A3626]" data-testid="cert-date">{dateStr}</div>
                <div className="border-t border-[#121614] mt-2 pt-2 font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#4B534E]">Date issued</div>
              </div>
            </div>

            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#4B534E] mt-10" data-testid="cert-id">
              Certificate No. {cert.certificate_id}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: landscape; margin: 0.5in; }
        }
      `}</style>
    </div>
  );
}
